
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	QueryList,
	ViewChild,
	ViewChildren,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';


import {
	catchError,
	distinctUntilChanged,
	finalize,
	takeUntil,
} from 'rxjs/operators';

import {
	Observable,
	Subject,
	of,
} from 'rxjs';


import { LoaderComponent } from 'ib/internetbank-core/ib-components/loader/loader.component';
import { BottomSheetService } from 'ib/internetbank-core/ib-components/pop-ups/bottom-sheet/bottom-sheet.service';
import { PopUpService } from 'ib/internetbank-core/ib-components/pop-ups/pop-up/pop-up.service';
import { ScreenMode, ScreenModeService } from 'ib/internetbank-core/ib-services/screen-mode.service';

import { ProductId } from '../../../../common/models/product-id';
import { ProductTypes } from '../../../../common/models/product-types';
import { PipeModule } from '../../../../common/pipes/pipe.module';
import AltLinksUtils from '../../../../common/utils/alt-link-utils';
import { BonusTransferTypeBottomSheetComponent } from '../../../../pages/products/bonuses/pages/details/components/bonus-transfer-type-bottom-sheet/bonus-transfer-type.content-component-for-bottom-sheet-component.component';
import { HiddenProductListFacade } from '../../../../pages/shared/products/hidden-product-list/hidden-product-list.facade';
import { TransferTabsForSelfTransfers } from '../../../../sections/operations/transfers/models/transfers.enums';
import { SelfTransferRouteUtils } from '../../../../sections/operations/transfers/route-utils/self-transfer.route-utils';
import { LayoutService } from '../../../../ui/layout/layout.service';
import { BonusTransferTypePopupComponent } from '../../../bonuses/components/bonus-transfer-type-popup/bonus-transfer-type.content-component-for-popup-component.component';
import { FastMenuItem, Product } from '../../../products.model';
import { ProductsService } from '../../../products.service';


import { InternetbankAppApplicationException } from './../../../../common/exceptions/exceptions';
import { CardStatusList } from './../../../../common/models/card-status-list';
import { ProductsRenamingService } from './../../../products-renaming.service';
import { MenuItem } from './menu-item';

@Component({
	selector: 'app-product-fast-menu',
	templateUrl: './product-fast-menu.component.html',
	styleUrls: ['./product-fast-menu.component.less'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		RouterModule,
		LoaderComponent,
		PipeModule,
	],
})
export class ProductFastMenuComponent implements OnInit, OnDestroy {

	@ViewChild(LoaderComponent, { static: true })
	public loader: LoaderComponent;

	@Input()

	public product: Product;

	@Output()
	public actionRouted: EventEmitter<void> = new EventEmitter();

	@Input()
	public productType: ProductTypes | null | undefined;

	@ViewChildren('menuItem')
	public menuItemList: QueryList<ElementRef<HTMLElement>>;

	public menu: Array<MenuItem> = [];

	private ngUnSubscribe: Subject<void> = new Subject();

	constructor(
		private productsService: ProductsService,
		private productsRenamingService: ProductsRenamingService,
		private layoutService: LayoutService,
		private screenModeService: ScreenModeService,
		private hiddenProductListFacade: HiddenProductListFacade,
		private popUpService: PopUpService,
		private bottomSheetService: BottomSheetService,
		private router: Router,
		private changeDetectorRef: ChangeDetectorRef,
	) { }


	public ngOnInit(): void {
		this.getFastMenu();

		if (this.product.cardStatusListSubject) {
			this.getFastMenuWhenCardStatusListChanged();
		}
	}

	public onKeyup(event: KeyboardEvent): void {

		if (event.code === 'Escape') {
			return;
		}

		if (this.menuItemList.last.nativeElement.children[0] === document.activeElement) {
			(this.menuItemList.first.nativeElement.children[0] as HTMLElement).focus();
			event.preventDefault();
			event.stopPropagation();
		}

	}

	public ngOnDestroy(): void {
		this.ngUnSubscribe.next();
		this.ngUnSubscribe.complete();
	}

	public routeActionClick(menuItem: MenuItem): void {
		this.actionRouted.next();
	}

	public showLoader(): void {
		this.loader.show();
	}

	public hideProduct(): void {
		this.showLoader();
		this.hiddenProductListFacade.hideProductAndUpdateProductList(this.product)
			.subscribe();
	}

	public deleteProduct(): void {
		this.showLoader();
		this.hiddenProductListFacade.deleteProductAndUpdateProductList(this.product)
			.subscribe();
	}

	public showProduct(): void {
		this.showLoader();
		this.hiddenProductListFacade.showProductAndUpdateProductList(this.product)
			.subscribe();
	}

	public renameProduct(): void {
		this.productsRenamingService.emitRenamingInitiated(this.product.productId?.getSimpleId() || undefined);
	}

	public generateAltLink(): void {
		AltLinksUtils.openLinkIntoNewWindow(
			'https://americanexpress.ru/personal/privilege/membership-rewards/catalog/',
		);
	}

	public hideProductsMenu(): void {
		this.layoutService.hideProductsMenu();
	}

	private getMenu(): Observable<Array<FastMenuItem | MenuItem>> {
		return (
			this.product.menu && this.productType === ProductTypes.BONUS
				? of(this.product.menu)
				: this.productsService.getProductFastMenu(this.product)
		)
			.pipe(
				finalize(() => {
					this.loader.hide();
					this.changeDetectorRef.markForCheck();
				}),
				catchError((error: InternetbankAppApplicationException) => of([])),
			);
	}

	private getFastMenu(): void {
		if (this.productType === ProductTypes.HIDDEN) {
			this.loader.hide();
			this.product.menu = [];
			this.product.menu.push({
				title: 'Показать',
				showAction: true,
			} as MenuItem);

			this.menu = this.product.menu as Array<MenuItem>;

			this.changeDetectorRef.markForCheck();
		} else {
			this.getMenu()
				.subscribe((data: Array<FastMenuItem>) => {
					this.product.menu = data.map((x: FastMenuItem) => {

						const menuItem: MenuItem = {
							title: x.linkText,
							actionType: x.actionType,
							routAction: null,
							queryParams: null,
							showAction: false,
							hideAction: false,
							deleteAction: false,
							altLink: false,
							renameAction: false,
						};

						if (
							this.product.productId &&
							!(this.product.productId instanceof ProductId)
						) {
							this.product.productId = ProductId.createIdByProdId(this.product.productId);
						}
						switch (x.actionType) {
							case 'CARD_TRANSFER':
								menuItem.routAction = SelfTransferRouteUtils.routeWithReplenishment.createNavigationParametersForReplenishmentFrom(TransferTabsForSelfTransfers.card, this.product.productId).routerLink;
								break;
							case 'INTERNAL_TRANSFER_ACCOUNT':
								menuItem.routAction = SelfTransferRouteUtils.routeWithReplenishment.createNavigationParametersForReplenishmentTo(TransferTabsForSelfTransfers.card, this.product.productId).routerLink;
								break;
							case 'INTERNAL_TRANSFER':
								{
									const productId: ProductId | undefined = this.product.productId;

									menuItem.routAction = this.screenModeService.screenMode !== ScreenMode.MOBILE
										? [
											'/payments/payment-own',
											{ target: this.product.productId?.toRouteParameter() },
										]
										: [
											'/mobile/payments/payment-own',
											{ target: this.product.productId?.toRouteParameter() },
										];
								}

								break;
							case 'PAY_CREDIT': {

								if (ProductId.isSavedExternalCard(this.product)) {
									menuItem.routAction = SelfTransferRouteUtils.routeBase.createNavigationParametersForPath(TransferTabsForSelfTransfers.credit).routerLink;
									menuItem.queryParams = { from: this.product.productId?.getSimpleId() };
								} else {
									const routerNavigationParameters = SelfTransferRouteUtils.routeWithReplenishment.createNavigationParametersForReplenishmentTo(TransferTabsForSelfTransfers.credit, this.product.productId);
									menuItem.routAction = routerNavigationParameters.routerLink;
								}
								break;
							}

							case 'CARD_BLOCK':
								menuItem.routAction = this.screenModeService.screenMode !== ScreenMode.MOBILE
									? [
										'/card',
										this.product.productId?.getSimpleId(),
										'card-block-wizard',
									]
									: [`/mobile/card/${this.product.productId?.getSimpleId()}/settings/card-block`];
								break;
							case 'CARD_UNLOCK':
								menuItem.routAction = menuItem.routAction = this.screenModeService.screenMode !== ScreenMode.MOBILE
									? [
										'/card',
										this.product.productId?.getSimpleId(),
										'card-unlock-wizard',
									]
									: [`/mobile/card/${this.product.productId?.getSimpleId()}/settings/card-unlock`];
								break;
							case 'REISSUE_CARD':
								menuItem.routAction = this.screenModeService.screenMode !== ScreenMode.MOBILE
									? [
										'/card/reissue',
										this.product.productId?.getSimpleId(),
									]
									: [`/mobile/card/${this.product.productId?.getSimpleId()}/settings/reissue`];
								break;
							case 'TRANSFER_VPC':
								menuItem.routAction = SelfTransferRouteUtils.routeWithReplenishment.createNavigationParametersForReplenishmentTo(TransferTabsForSelfTransfers.card, this.product.productId).routerLink;
								break;
							case 'HIDE_PRODUCT':
								menuItem.routAction = null;
								menuItem.hideAction = true;
								break;
							case 'DELETE_PRODUCT':
								menuItem.routAction = null;
								menuItem.deleteAction = true;
								break;
							case 'SHOW_PRODUCT':
								menuItem.routAction = null;
								menuItem.showAction = true;
								break;
							// bonuses
							case 'PAY':
								menuItem.routAction = ['/operations/with-bonuses'];
								menuItem.queryParams = this.product.productId;
								break;
							case 'TRANSFER_TO_YOURSELF':
								{

									if (this.screenModeService.screenMode === ScreenMode.MOBILE) {

										switch (this.product.productId?.productType) {
											case ProductTypes.BONUS:
												menuItem.routAction = [
													'/mobile/payments/payment-own',
													{ source: this.product.productId?.toRouteParameter() },
												];
												break;

											default:
												menuItem.routAction = [
													'/mobile/payments/payment-own',
													{ target: this.product.productId?.toRouteParameter() },
												];
												break;
										}

									} else {

										switch (this.product.productId?.productType) {
											case ProductTypes.BONUS:
												menuItem.routAction = [
													'/payments/payment-own',
													{ source: this.product.productId?.toRouteParameter() },
												];
												break;

											default:
												menuItem.routAction = [
													'/payments/payment-own',
													{ target: this.product.productId?.toRouteParameter() },
												];
												break;
										}

									}

								}
								break;

							case 'REFUND_OPERATION':
								menuItem.routAction = [
									'/bonus/amends',
									this.product.programId,
								];
								break;
							case 'ORDER_REWARD':
								menuItem.routAction = null;
								menuItem.altLink = true;
								break;
							case 'TRANSFER_TO_OTHER_PROGRAM':
							case 'BONUS_TRANSFER_TO_FUSION_PARTNER':
								menuItem.routAction = [
									'/bonus/change-program',
									this.product.programId,
								];
								break;
							case 'RENAME_PRODUCT':
								menuItem.renameAction = true;
								break;
							// бонусная программа - Накопить
							case 'ACCUMULATE':
								menuItem.routAction = ['/promotions'];
								break;
							case 'TRANSFER':
								menuItem.onClick = () => {
									console.log(this.screenModeService.screenMode === ScreenMode.MOBILE)
									if (this.screenModeService.screenMode === ScreenMode.MOBILE) {
										const componentRefForComponent = this.bottomSheetService.open(
											BonusTransferTypeBottomSheetComponent,
											{
												inputs: {
													productId: this.product.productId,
													programId: this.product.programId,
													closePopup: () => {
														componentRefForComponent.componentRef.instance.close()
													},
												},
											},
										);
									} else {
										const componentRefForComponent = this.popUpService.open(
											BonusTransferTypePopupComponent,
											{
												inputs: {
													productId: this.product.productId,
													programId: this.product.programId,
													closePopup: () => {
														componentRefForComponent.componentRef.instance.close()
													},
												},
											},
										);
									}
								}
								break;
							default:
								menuItem.routAction = menuItem.routAction = [x.actionType];
								break;
						}

						return menuItem;
					});

					this.menu = this.product.menu as Array<MenuItem>;

				});
		}
	}

	private getFastMenuWhenCardStatusListChanged(): void {
		(this.product.cardStatusListSubject as Subject<Array<CardStatusList>>).asObservable()
			.pipe(
				distinctUntilChanged((prev: Array<CardStatusList>, curr: Array<CardStatusList>) => JSON.stringify(prev) === JSON.stringify(curr)),
				takeUntil(this.ngUnSubscribe),
			)
			.subscribe(() => this.getFastMenu());
	}
}
